import React from 'react';
import { Card, CardContent } from './ui/card';
import { MapPin, Home, CreditCard, CheckCircle } from 'lucide-react';

const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      icon: MapPin,
      title: "Secure your desired plot and location",
      description: "Choose your preferred area and plot size. We help verify title, utilities and access so your project starts clean and compliant.",
    },
    {
      number: "02",
      icon: Home,
      title: "Choose your house design",
      description: "Pick from studio, 1BR, 2BR, or 3BR options, or tailor a layout to your budget and land. Get clear drawings and a realistic build timeline.",
    },
    {
      number: "03",
      icon: CreditCard,
      title: "Choose your payment plan",
      description: "Select daily, weekly, or monthly contributions that match your income flow. We provide transparent milestones and costs from day one.",
    },
    {
      number: "04",
      icon: CheckCircle,
      title: "Meet the requirements to qualify for the plan",
      description: "Submit basic KYC, proof of income and plot documentation. Our team supports you in closing any gaps quickly to get started.",
    },
  ];

  return (
    <section className="py-12 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light title-font text-stone-800 mb-4 md:mb-6">
            Your Clear Path to Homeownership
          </h2>
          <p className="text-stone-600 text-base md:text-lg max-w-3xl mx-auto px-4">
            Our process is simple, transparent, and designed to make your dream home a reality, even if you have an existing plot of land.
          </p>
        </div>

        <div className="space-y-8 md:space-y-12">
          {steps.map((step, index) => {
            const IconComponent = step.icon;
            const isEven = index % 2 === 1;

            return (
              <div key={index} className={`flex flex-col lg:flex-row items-center gap-6 md:gap-12 ${isEven ? 'lg:flex-row-reverse' : ''}`}>
                <div className="flex-1 w-full">
                  <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-4 md:p-6 lg:p-8">
                      <div className="flex flex-col sm:flex-row items-start gap-4 md:gap-6">
                        <div className="flex-shrink-0 mx-auto sm:mx-0">
                          <div className="w-12 h-12 md:w-16 md:h-16 bg-amber-100 rounded-full flex items-center justify-center">
                            <IconComponent className="w-6 h-6 md:w-8 md:h-8 text-[#81be00]" />
                          </div>
                        </div>
                        <div className="flex-1 text-center sm:text-left">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 md:gap-4 mb-3 md:mb-4">
                            <span className="text-2xl md:text-3xl lg:text-4xl font-light text-[#81be00] title-font">
                              {step.number}
                            </span>
                            <h3 className="text-lg md:text-xl lg:text-2xl font-medium title-font text-stone-800">
                              {step.title}
                            </h3>
                          </div>
                          {step.description ? (
                            <p className="text-stone-600 text-sm md:text-base leading-relaxed">{step.description}</p>
                          ) : null}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="flex-shrink-0 hidden lg:block">
                  <div className="w-20 h-2 bg-[#81be00] rounded-full"></div>
                </div>

                {/* Mobile connector */}
                <div className="flex-shrink-0 lg:hidden">
                  <div className="w-2 h-8 md:h-12 bg-[#81be00] rounded-full"></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
